import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Autocomplete,
  createFilterOptions
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Close as CloseIcon, Add as AddIcon, Label as LabelIcon } from '@mui/icons-material';
import PlatformDialog from '../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../molecules/PlatformDialog/PlatformDialogTitle';
import api from '../../../services/api';
import TagChip from '../../atoms/TagChip';
import { useViewSettings } from '../../../context/ViewSettingsContext';
import { operationalScrollbars } from '../../../design-system/tokens';

const filter = createFilterOptions();

const TagDialog = ({
  open,
  onClose,
  onAddTags,
  availableTags = [],
  currentTags = [],
  mode = 'select' // 'select', 'create', or 'both'
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;
  const [selectedTags, setSelectedTags] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [allTags, setAllTags] = useState(availableTags);
  const { fontSize } = useViewSettings();
  let customFontSize = undefined;
  if (fontSize) {
    const match = fontSize.match(/([\d.]+)(rem|px|em)?/);
    if (match) {
      const value = parseFloat(match[1]);
      const unit = match[2] || 'rem';
      customFontSize = (value * 0.8) + unit;
    }
  }

  useEffect(() => {
    if (open) {
      setSelectedTags([]);
      setErrorMessage(null);
      // Buscar todas as tags do backend
      api.get('/tags').then(res => {
        setAllTags(res.data.data || []);
      }).catch(() => {
        setAllTags(availableTags);
      });
    }
  }, [open, availableTags]);

  const handleClose = () => {
    setSelectedTags([]);
    setErrorMessage(null);
    onClose();
  };

  const handleCreateTag = async (tagName) => {
    // Garantir que tagName seja uma string válida e não-vazia
    if (!tagName || typeof tagName !== 'string' || !tagName.trim()) {
      return;
    }

    const nameToCreate = tagName.trim();

    try {
      setIsCreating(true);
      setErrorMessage(null);

      // Criar a nova tag no backend (Sincronizado com a lógica que funciona nos cards)
      const res = await api.post('/tags', { name: nameToCreate });
      
      const newTag = res.data.data;
      
      // Atualizar estados locais
      setSelectedTags(prev => [...prev, newTag]);
      setAllTags(prev => [...prev, newTag]);
      
      // Limpar erro se ocorreu anteriormente
      setErrorMessage(null);

    } catch (err) {
      // Se for erro de duplicidade, buscar a tag existente e adicionar à seleção
      const msg = err.response?.data?.message || err.message || '';
      
      if (msg.toLowerCase().includes('já possui uma tag')) {
        const existingTag = allTags.find(tag => tag.name.toLowerCase() === nameToCreate.toLowerCase());
        if (existingTag) {
          if (!selectedTags.some(tag => tag._id === existingTag._id)) {
            setSelectedTags(prev => [...prev, existingTag]);
          }
          setErrorMessage(null);
          return;
        }
      }
      
      setErrorMessage(msg || 'Erro ao criar tag');
      console.error('Erro na criação de tag:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedTags.length === 0) {
      setErrorMessage('Selecione pelo menos uma tag');
      return;
    }

    try {
      await onAddTags(selectedTags);
      handleClose();
    } catch (err) {
      setErrorMessage(err.message);
    }
  };

  // Função para excluir uma tag
  const handleDeleteTag = async (tag) => {
    if (!tag || !tag._id) {
      console.error('Tentativa de remover tag inválida:', tag);
      setErrorMessage('Tag inválida para remoção.');
      return;
    }

    if (!window.confirm(`Tem certeza que deseja excluir a tag "${tag.name}"?`)) return;
    
    try {
      await api.delete(`/tags/${tag._id}`);
      setAllTags(tags => tags.filter(t => t._id !== tag._id));
      setSelectedTags(tags => tags.filter(t => t._id !== tag._id));
      setErrorMessage(null);
    } catch (err) {
      console.error('Erro ao excluir tag:', err, tag);
      const errorMsg = err.response?.data?.message || err.message || 'Erro desconhecido ao excluir tag';
      setErrorMessage(`Não foi possível excluir a tag "${tag.name}". ${errorMsg}`);
    }
  };

  // Filtrar opções para não mostrar tags já selecionadas e ordenar alfabeticamente
  const currentTagIds = new Set(
    currentTags
      .map(tag => (typeof tag === 'object' ? tag._id : tag))
      .filter(Boolean)
  );
  const currentTagNames = new Set(
    currentTags
      .map(tag => (typeof tag === 'object' ? (tag.name || tag.label) : tag))
      .filter(Boolean)
      .map(name => name.toLowerCase())
  );

  const filteredOptions = allTags
    .filter(tag => !selectedTags.some(sel => sel._id === tag._id))
    .filter(tag => !currentTagIds.has(tag._id))
    .filter(tag => !currentTagNames.has((tag.name || '').toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt', { sensitivity: 'base' }));

  return (
    <PlatformDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableScrollLock={true}
    >
      <PlatformDialogTitle
        title="Adicionar Tags"
        icon={<LabelIcon />}
        onClose={handleClose}
      />

      <DialogContent sx={{ pt: 3, pb: 2, px: 3, ...operationalScrollbars.default }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {(mode === 'select' || mode === 'both') && (
          <Box sx={{ mb: 2, mt: 1 }}>
            {/* Tags selecionadas com X de remover */}
            {selectedTags.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Tags Selecionadas
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedTags.map(tag => (
                    <TagChip
                      key={tag._id}
                      label={tag.name}
                      onDelete={() => setSelectedTags(prev => prev.filter(t => t._id !== tag._id))}
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            <Autocomplete
              multiple
              id="tags-select"
              freeSolo={mode === 'both' || mode === 'create'}
              options={filteredOptions}
              loading={isCreating}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if (option.inputValue) return option.inputValue;
                return option.name;
              }}
              ListboxProps={{
                sx: {
                  ...operationalScrollbars.default,
                  '& .MuiAutocomplete-option': {
                    backgroundColor: 'background.paper',
                    backdropFilter: 'none',
                    WebkitBackdropFilter: 'none',
                    marginBottom: '4px',
                    marginLeft: '4px',
                    marginRight: '4px',
                    borderRadius: '6px',
                    border: `1px solid ${theme.palette.divider}`,
                    transition: 'background-color 0.16s ease, border-color 0.16s ease',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'rgba(0, 0, 0, 0.08)',
                    },
                    '&.Mui-focused, &[aria-selected="true"]': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(144, 202, 249, 0.16)'
                        : 'rgba(25, 118, 210, 0.12)',
                      border: `1px solid ${theme.palette.primary.main}`,
                    }
                  }
                }
              }}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);

                // Se mode permite criar e há texto digitado
                if ((mode === 'both' || mode === 'create') && params.inputValue !== '') {
                  const inputLower = params.inputValue.toLowerCase().trim();
                  const exists = allTags.some(
                    tag => tag.name.toLowerCase() === inputLower
                  );

                  if (!exists && inputLower.length > 0) {
                    filtered.push({
                      inputValue: params.inputValue.trim(),
                      name: `Criar "${params.inputValue.trim()}"`,
                      isNew: true
                    });
                  }
                }

                return filtered;
              }}
              value={[]}
              onChange={(event, newValue) => {
                if (newValue.length > 0) {
                  const last = newValue[newValue.length - 1];

                  // Se é uma nova tag (opção "Criar X")
                  if (last && last.isNew) {
                    handleCreateTag(last.inputValue);
                  } else if (last && last._id && !selectedTags.some(t => t._id === last._id)) {
                    setSelectedTags([...selectedTags, last]);
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder={mode === 'both' || mode === 'create' ? "Digite o nome da tag..." : "Escolha as tags"}
                  helperText={mode === 'both' || mode === 'create' ? "Pressione Enter para criar uma nova tag" : undefined}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (mode === 'both' || mode === 'create')) {
                      const inputValue = e.target.value?.trim();
                      if (inputValue) {
                        // Verificar se já existe
                        const exists = allTags.some(
                          tag => tag.name.toLowerCase() === inputValue.toLowerCase()
                        );
                        if (!exists) {
                          e.preventDefault();
                          handleCreateTag(inputValue);
                          // Limpar o input após criar
                          e.target.value = '';
                        }
                      }
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isCreating ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key || option._id || option.inputValue} {...otherProps}>
                    {option.isNew ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddIcon fontSize="small" color="primary" />
                        <Typography color="primary">{option.name}</Typography>
                      </Box>
                    ) : (
                      option.name
                    )}
                  </li>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...otherProps } = getTagProps({ index });
                  return (
                    <TagChip
                      key={key}
                      label={option.name}
                      onDelete={option._id ? () => handleDeleteTag(option) : undefined}
                      variant="filled"
                      size="medium"
                      fontSize={customFontSize}
                      {...otherProps}
                    />
                  );
                })
              }
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1.5 }}>
        <Button
          onClick={handleClose}
          variant="text"
          sx={{
            minWidth: 80,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={selectedTags.length === 0}
          sx={{
            minWidth: 80,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Adicionar
        </Button>
      </DialogActions>
    </PlatformDialog>
  );
};

TagDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddTags: PropTypes.func.isRequired,
  availableTags: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  })),
  currentTags: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      label: PropTypes.string
    })
  ])),
  mode: PropTypes.oneOf(['select', 'create', 'both'])
};

export default TagDialog; 
